/**
 * The validation issue shown on one editable cell, or null while it must stay hidden.
 *
 *   EditableGridCell ──┐
 *                      ├──► useEditableGridIssue ──► useEditableGridRows
 *   PopoverEditCell  ──┘
 *
 * Lives in its own file so those two consumers do not import each other:
 * `PopoverEditCell` needs this hook and `EditableGridCell` renders
 * `PopoverEditCell`, which is a cycle when both sit in one module — exactly the
 * load-order class `web-grids-no-circular` forbids (utils/grid/cell/cellExit.ts banner).
 */
import { useMemo } from 'react';
import { useDebounce } from 'usehooks-ts';
import { ZodError } from 'zod';

import { useEditableGridRows } from './useEditableGridRows';

export const useEditableGridIssue = (rowId: number, field: string): { message: string; warning?: true } | null => {
  const validation = useEditableGridRows((store) => store.getRowError(rowId as number));
  const canDisplayError = useEditableGridRows((store) => {
    const interactionModel = store.rowInteractionModel[rowId];

    return interactionModel?.saved || interactionModel?.visited.has(field);
  });
  // work around for allowing the state to settle, and not flash an error.
  const debouncedDisplayError = useDebounce(canDisplayError, 10);
  const displayError = canDisplayError && debouncedDisplayError;

  return useMemo(() => {
    if (!displayError) {
      return null;
    }

    if (validation instanceof ZodError) {
      return validation?.issues?.find((issue) => issue.path[0] === field) ?? null;
    }

    return validation ?? null;
  }, [validation, field, displayError]);
};
